import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { useLanguage } from '../../context/LanguageContext';

const LanguageSelector = ({ variant = 'dropdown', className = '' }) => {
  const { lang, setLang, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (variant === 'grid') {
    return (
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 ${className}`}>
        {languages.map((l) => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm font-medium transition-all ${
              lang === l.code
                ? 'bg-accent/10 border-accent/30 text-accent'
                : 'bg-card border-border text-foreground hover:border-accent/20'
            }`}
          >
            <span className="w-7 h-5 bg-card-foreground/5 rounded text-[10px] font-bold flex items-center justify-center uppercase text-muted-foreground">
              {l.code}
            </span>
            <span className="truncate">{l.label}</span>
            {lang === l.code && (
              <Icon name="Check" size={14} className="ml-auto text-accent flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
    );
  }

  // Default: compact dropdown
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card-foreground/5 transition-colors text-sm"
      >
        <span className="w-6 h-4 bg-card-foreground/10 rounded text-[9px] font-bold flex items-center justify-center uppercase">
          {currentLang.code}
        </span>
        <Icon name="ChevronDown" size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-44 bg-card border border-border rounded-xl shadow-xl z-50 py-1 overflow-hidden animate-in fade-in slide-in-from-top-1">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Language</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setIsOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  lang === l.code
                    ? 'bg-accent/10 text-accent font-semibold'
                    : 'text-foreground hover:bg-card-foreground/5'
                }`}
              >
                <span className="w-6 h-4 bg-card-foreground/10 rounded text-[9px] font-bold flex items-center justify-center uppercase text-muted-foreground">
                  {l.code}
                </span>
                <span className="flex-1 text-left">{l.label}</span>
                {lang === l.code && <Icon name="Check" size={14} className="text-accent" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
